import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from './authContext';
import { useMediaSettings } from './MediaSettingsContext';

// Call states
export const CALL_STATE = {
  IDLE: 'idle',
  INCOMING: 'incoming',
  OUTGOING: 'outgoing',
  CONNECTING: 'connecting',
  IN_CALL: 'in_call',
  ENDED: 'ended',
};

const CallContext = createContext(null);

export const useCall = () => useContext(CallContext);

// Signaling WS URL (same as mediasoup)
const SIGNALING_URL = import.meta.env.VITE_VCAFE_SIGNALING_URL || 'ws://localhost:5000';

export const CallProvider = ({ children }) => {
  const { user } = useAuth();
  const mediaSettings = useMediaSettings();

  // Call state
  const [callState, setCallState] = useState(CALL_STATE.IDLE);
  const [currentCall, setCurrentCall] = useState(null);
  // { callId, mode: 'audio'|'video', thread: { id, type, name, memberIds }, roomId, participants: [] }

  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState(new Map()); // userId -> MediaStream
  const [participants, setParticipants] = useState([]); // [{ userId, name, avatar, isMuted, hasVideo }]

  // Controls
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Incoming call queue (for multiple incoming)
  const [incomingCalls, setIncomingCalls] = useState([]);

  // Active calls by thread (for "Join call" button in group threads)
  // threadId -> { callId, roomId, mode, participantCount }
  const [activeCallsByThread, setActiveCallsByThread] = useState({});

  // WebSocket ref for signaling
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  // Refs to avoid unnecessary reconnections when state changes
  const callStateRef = useRef(callState);
  const currentCallRef = useRef(currentCall);
  
  useEffect(() => {
    callStateRef.current = callState;
    currentCallRef.current = currentCall;
  }, [callState, currentCall]);

  // Generate unique call ID
  const generateCallId = () => `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Connect to signaling server for call control
  const connectSignaling = useCallback(() => {
    if (!user?.id) return;
    // Don't create new connection if one is already OPEN or CONNECTING
    if (wsRef.current?.readyState === WebSocket.OPEN || 
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    console.log('[CallContext] Attempting WebSocket connection to:', SIGNALING_URL);
    const ws = new WebSocket(SIGNALING_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[CallContext] WebSocket connected successfully');
      // Authenticate with userId for call routing
      ws.send(JSON.stringify({ type: 'call-auth', userId: user.id }));
    };

    ws.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }

      switch (msg.type) {
        case 'call-invite': {
          // Incoming call from someone
          const { callId, fromUserId, fromUserName, fromUserAvatar, threadId, threadType, threadName, mode, roomId, isGroupCall } = msg;
          
          // For group calls, track as active call for the thread (opt-in join)
          if (isGroupCall && threadId) {
            setActiveCallsByThread(prev => ({
              ...prev,
              [threadId]: { callId, roomId, mode, threadName, participantCount: 1 }
            }));
          }
          
          // Add to incoming calls queue
          setIncomingCalls(prev => [
            ...prev.filter(c => c.callId !== callId),
            { callId, fromUserId, fromUserName, fromUserAvatar, threadId, threadType, threadName, mode, roomId, isGroupCall, receivedAt: Date.now() }
          ]);
          // If idle, switch to incoming state (for 1:1 calls, show immediately; for group calls, also show but user can dismiss)
          if (callStateRef.current === CALL_STATE.IDLE) {
            setCallState(CALL_STATE.INCOMING);
          }
          break;
        }

        case 'call-accepted': {
          // Remote party accepted our call
          const { callId, userId: acceptedByUserId } = msg;
          if (currentCallRef.current?.callId === callId) {
            setCallState(CALL_STATE.CONNECTING);
            // Will proceed to join mediasoup room
          }
          break;
        }

        case 'call-rejected': {
          // Remote party rejected
          const { callId, userId: rejectedByUserId, reason } = msg;
          if (currentCallRef.current?.callId === callId) {
            // If 1:1, end call. If group, just note the rejection.
            if (currentCallRef.current.thread?.type === 'direct') {
              endCallCleanup();
            }
          }
          break;
        }

        case 'call-ended': {
          // Call ended by someone
          const { callId, threadId } = msg;
          if (currentCallRef.current?.callId === callId) {
            endCallCleanup();
          }
          // Also remove from incoming if present
          setIncomingCalls(prev => prev.filter(c => c.callId !== callId));
          // Remove from active calls by thread
          if (threadId) {
            setActiveCallsByThread(prev => {
              const next = { ...prev };
              delete next[threadId];
              return next;
            });
          } else {
            // If no threadId provided, search by callId
            setActiveCallsByThread(prev => {
              const next = { ...prev };
              Object.keys(next).forEach(tid => {
                if (next[tid]?.callId === callId) delete next[tid];
              });
              return next;
            });
          }
          break;
        }

        case 'call-participant-joined': {
          const { callId, userId: joinedUserId, userName, userAvatar } = msg;
          if (currentCallRef.current?.callId === callId) {
            setParticipants(prev => {
              if (prev.some(p => p.userId === joinedUserId)) return prev;
              return [...prev, { userId: joinedUserId, name: userName, avatar: userAvatar, isMuted: false, hasVideo: true }];
            });
          }
          break;
        }

        case 'call-participant-left': {
          const { callId, userId: leftUserId } = msg;
          if (currentCallRef.current?.callId === callId) {
            setParticipants(prev => prev.filter(p => p.userId !== leftUserId));
            setRemoteStreams(prev => {
              const next = new Map(prev);
              next.delete(leftUserId);
              return next;
            });
          }
          break;
        }

        case 'call-in-progress': {
          // Notification that a group call is active (for opt-in join)
          // This will be handled by UI showing "Join call" button in thread header
          break;
        }

        case 'call-active-status': {
          // Response to call-get-active query
          const { threadId, activeCall } = msg;
          if (threadId) {
            if (activeCall) {
              setActiveCallsByThread(prev => ({
                ...prev,
                [threadId]: activeCall
              }));
            } else {
              setActiveCallsByThread(prev => {
                const next = { ...prev };
                delete next[threadId];
                return next;
              });
            }
          }
          break;
        }

        default:
          break;
      }
    };

    ws.onerror = (error) => {
      console.error('[CallContext] WS error:', error);
      console.error('[CallContext] WS readyState:', ws.readyState);
    };

    ws.onclose = (event) => {
      console.log('[CallContext] WS closed. Code:', event.code, 'Reason:', event.reason, 'Clean:', event.wasClean);
      console.log('[CallContext] WS readyState at close:', ws.readyState);
      // Attempt reconnect after delay
      reconnectTimeoutRef.current = setTimeout(() => {
        console.log('[CallContext] Attempting reconnect...');
        connectSignaling();
      }, 3000);
    };

    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      ws.close();
    };
  }, [user?.id]); // Only reconnect when user changes, not when callState/currentCall change

  // Connect on mount when user is available
  useEffect(() => {
    if (user?.id) {
      connectSignaling();
    }
    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      // Only close if connection is actually OPEN, not if still CONNECTING
      // This prevents React StrictMode double-invoke from causing 1006 errors
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [user?.id, connectSignaling]);

  // Send message via WS
  const sendSignaling = useCallback((msg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  // Acquire local media with better error handling - uses MediaSettings for device selection + virtual background
  const acquireMedia = useCallback(async (mode) => {
    try {
      // Check if mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Media devices not supported in this browser');
      }

      // Use MediaSettings constraints if available, otherwise fallback to defaults
      let constraints;
      if (mediaSettings?.getMediaConstraints) {
        constraints = mediaSettings.getMediaConstraints({ mode });
      } else {
        // Fallback constraints
        constraints = {
          audio: true,
          video: mode === 'video' ? { width: { ideal: 1280 }, height: { ideal: 720 } } : false,
        };
      }
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Apply virtual background if enabled and this is a video call
      if (mode === 'video' && mediaSettings?.applyVirtualBackground) {
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          try {
            const processedTrack = await mediaSettings.applyVirtualBackground(videoTrack);
            if (processedTrack && processedTrack !== videoTrack) {
              // Replace the original video track with the processed one
              stream.removeTrack(videoTrack);
              stream.addTrack(processedTrack);
              // Stop the original track since we're using the processed one
              videoTrack.stop();
            }
          } catch (vbErr) {
            console.warn('[CallContext] Virtual background failed, using original video:', vbErr);
          }
        }
      }
      
      setLocalStream(stream);
      return stream;
    } catch (err) {
      console.error('[CallContext] Failed to acquire media:', err);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to access camera/microphone';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Camera/microphone permission denied. Please allow access in your browser settings.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'No camera or microphone found. Please connect a device.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'Camera/microphone is already in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Camera does not meet the required constraints.';
      }
      
      // Show alert for now - could be replaced with toast notification
      alert(errorMessage);
      throw err;
    }
  }, [mediaSettings]);

  // Release local media
  const releaseMedia = useCallback(() => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }
  }, [localStream]);

  // Start a call (outgoing)
  const startCall = useCallback(async ({ thread, mode = 'video' }) => {
    if (callState !== CALL_STATE.IDLE) {
      console.warn('[CallContext] Already in a call');
      return;
    }

    const callId = generateCallId();
    const roomId = `call:${callId}`;

    // Set outgoing state immediately for responsive UI
    setCallState(CALL_STATE.OUTGOING);
    setCurrentCall({
      callId,
      mode,
      thread,
      roomId,
      isInitiator: true,
    });

    try {
      // Acquire media first
      await acquireMedia(mode);

      // Send call invite to participants
      if (thread.type === 'direct') {
        // 1:1 call - invite the other user
        sendSignaling({
          type: 'call-create',
          callId,
          roomId,
          mode,
          threadId: thread.id,
          threadType: 'direct',
          threadName: thread.name,
          targetUserId: thread.id, // For direct, thread.id is the other user's ID
        });
      } else {
        // Group call - create call and notify group (opt-in)
        sendSignaling({
          type: 'call-create',
          callId,
          roomId,
          mode,
          threadId: thread.id,
          threadType: thread.type,
          threadName: thread.name,
          memberIds: thread.memberIds || [],
        });
      }
    } catch (err) {
      // Failed to acquire media
      setCallState(CALL_STATE.IDLE);
      setCurrentCall(null);
      throw err;
    }
  }, [callState, acquireMedia, sendSignaling]);

  // Accept incoming call
  const acceptCall = useCallback(async (callId) => {
    const incoming = incomingCalls.find(c => c.callId === callId);
    if (!incoming) return;

    setCallState(CALL_STATE.CONNECTING);
    setCurrentCall({
      callId: incoming.callId,
      mode: incoming.mode,
      thread: {
        id: incoming.threadId,
        type: incoming.threadType,
        name: incoming.threadName,
      },
      roomId: incoming.roomId,
      isInitiator: false,
    });

    // Remove from incoming queue
    setIncomingCalls(prev => prev.filter(c => c.callId !== callId));

    try {
      await acquireMedia(incoming.mode);

      sendSignaling({
        type: 'call-accept',
        callId,
        roomId: incoming.roomId,
      });
    } catch (err) {
      endCallCleanup();
      throw err;
    }
  }, [incomingCalls, acquireMedia, sendSignaling]);

  // Reject incoming call
  const rejectCall = useCallback((callId, reason = 'declined') => {
    sendSignaling({
      type: 'call-reject',
      callId,
      reason,
    });
    setIncomingCalls(prev => prev.filter(c => c.callId !== callId));
    if (incomingCalls.length <= 1 && callState === CALL_STATE.INCOMING) {
      setCallState(CALL_STATE.IDLE);
    }
  }, [incomingCalls, callState, sendSignaling]);

  // End current call
  const endCall = useCallback(() => {
    if (currentCall) {
      sendSignaling({
        type: 'call-end',
        callId: currentCall.callId,
      });
    }
    endCallCleanup();
  }, [currentCall, sendSignaling]);

  // Cleanup after call ends
  const endCallCleanup = useCallback(() => {
    releaseMedia();
    setCallState(CALL_STATE.IDLE);
    setCurrentCall(null);
    setParticipants([]);
    setRemoteStreams(new Map());
    setIsMuted(false);
    setIsCameraOff(false);
    setIsMinimized(false);
  }, [releaseMedia]);

  // Join an ongoing group call (opt-in)
  const joinCall = useCallback(async ({ callId, roomId, mode, thread }) => {
    if (callState !== CALL_STATE.IDLE) {
      console.warn('[CallContext] Already in a call');
      return;
    }

    setCallState(CALL_STATE.CONNECTING);
    setCurrentCall({
      callId,
      mode,
      thread,
      roomId,
      isInitiator: false,
    });

    try {
      await acquireMedia(mode);

      sendSignaling({
        type: 'call-join',
        callId,
        roomId,
      });
    } catch (err) {
      endCallCleanup();
      throw err;
    }
  }, [callState, acquireMedia, sendSignaling, endCallCleanup]);

  // Toggle mute
  const toggleMic = useCallback(() => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  }, [localStream]);

  // Toggle camera
  const toggleCamera = useCallback(() => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsCameraOff(!videoTrack.enabled);
      }
    }
  }, [localStream]);

  // Minimize/restore overlay
  const minimize = useCallback(() => setIsMinimized(true), []);
  const restore = useCallback(() => setIsMinimized(false), []);

  // Update remote streams (called from mediasoup hook integration)
  const updateRemoteStreams = useCallback((streams) => {
    setRemoteStreams(streams);
  }, []);

  // Set call state to in_call (called when mediasoup connection is ready)
  const setInCall = useCallback(() => {
    setCallState(CALL_STATE.IN_CALL);
  }, []);

  // Dismiss incoming call notification without rejecting (for group calls)
  const dismissIncoming = useCallback((callId) => {
    setIncomingCalls(prev => prev.filter(c => c.callId !== callId));
    if (incomingCalls.length <= 1 && callState === CALL_STATE.INCOMING) {
      setCallState(CALL_STATE.IDLE);
    }
  }, [incomingCalls, callState]);

  // Check for active call in a thread (queries server)
  const checkActiveCall = useCallback((threadId) => {
    sendSignaling({
      type: 'call-get-active',
      threadId,
    });
  }, [sendSignaling]);

  // Get active call for a thread (from local state)
  const getActiveCallForThread = useCallback((threadId) => {
    return activeCallsByThread[threadId] || null;
  }, [activeCallsByThread]);

  const value = {
    // State
    callState,
    currentCall,
    localStream,
    remoteStreams,
    participants,
    incomingCalls,
    activeCallsByThread,
    isMuted,
    isCameraOff,
    isMinimized,

    // Actions
    startCall,
    acceptCall,
    rejectCall,
    dismissIncoming,
    endCall,
    joinCall,
    toggleMic,
    toggleCamera,
    minimize,
    restore,
    checkActiveCall,
    getActiveCallForThread,

    // Internal (for mediasoup integration)
    updateRemoteStreams,
    setInCall,
    sendSignaling,
  };

  return (
    <CallContext.Provider value={value}>
      {children}
    </CallContext.Provider>
  );
};

export default CallContext;
